// Dev-panel interceptors — only active when window.__cortexDev() returns true.
import { formatTimeWithSeconds } from '../utils/formatters.js'

function ts() {
    return formatTimeWithSeconds(new Date())
}
function tryParse(d) {
    try { return typeof d === 'string' ? JSON.parse(d) : d } catch { return d }
}
function truncate(d) {
    if (!d) return d
    try {
        const s = JSON.stringify(d)
        if (s.length < 12000) return d
        return { _truncated: true, preview: s.slice(0, 12000) + '...' }
    } catch { return d }
}

// Identify which system a URL belongs to
function detectSource(url) {
    if (!url) return 'HTTP'
    if (url.includes('/broadcasting/'))   return 'REVERB'
    if (url.includes('/sanctum/'))        return 'AUTH'
    if (url.startsWith('/api/'))          return 'API'
    return (url.split('/')[1] ?? 'HTTP').toUpperCase()
}

// Strip /api/v1 prefix so labels are shorter
function cleanPath(url) {
    return url?.replace(/^\/api\/v1/, '') ?? url
}

function pushEntry(type, label, detail, status, devId, source) {
    import('../stores/devLog.js').then(m => {
        const s = m.useDevLogStore()
        if ((type === 'res' || type === 'err') && devId) {
            const req = s.entries.find(e => e._devId === devId)
            if (req) {
                req.type   = type
                req.label  = label
                req.status = status
                req.source = source ?? req.source
                req.detail = { request: req.detail, response: detail }
                return
            }
        }
        const entry = { id: crypto.randomUUID(), time: ts(), type, label, detail, status, source: source ?? null }
        if (type === 'req') entry._devId = devId
        s.entries.unshift(entry)
        if (s.entries.length > 200) s.entries.length = 200
    }).catch(() => {})
}

// Patch api.request() BEFORE the async interceptor chain so the call stack
// captured in __devStack still contains the Vue component that made the call.
export function patchAxiosForStack(api) {
    if (api.__devStackPatched) return
    api.__devStackPatched = true
    const orig = api.request.bind(api)
    api.request = function(configOrUrl, config) {
        if (window.__cortexDev?.()) {
            const stack = new Error().stack
            if (typeof configOrUrl === 'string') {
                config = Object.assign(config ?? {}, { __devStack: stack })
                return orig(configOrUrl, config)
            } else {
                configOrUrl = Object.assign(configOrUrl ?? {}, { __devStack: stack })
                return orig(configOrUrl)
            }
        }
        return orig(configOrUrl, config)
    }
}

export function attachDevInterceptors(api) {
    api.interceptors.request.use(cfg => {
        if (!window.__cortexDev?.()) return cfg
        cfg._devId  = crypto.randomUUID()
        cfg._devT0  = Date.now()
        const body  = cfg.data ? tryParse(cfg.data) : null
        const src   = detectSource(cfg.url)
        pushEntry('req',
            `${(cfg.method ?? 'GET').toUpperCase()} ${cleanPath(cfg.url)}`,
            { params: cfg.params ?? null, body },
            null,
            cfg._devId,
            src,
        )
        return cfg
    }, err => Promise.reject(err))

    api.interceptors.response.use(res => {
        if (!window.__cortexDev?.()) return res
        const ms   = Date.now() - (res.config._devT0 ?? 0)
        const body = res.data
        const src  = detectSource(res.config.url)
        const meth = (res.config.method ?? 'GET').toUpperCase()
        const path = cleanPath(res.config.url)

        // res entry — no status in label, it goes in the status chip
        pushEntry('res',
            `${meth} ${path} (${ms}ms)`,
            truncate(body),
            res.status,
            res.config._devId,
            src,
        )

        // PHP debug entry — separate row with query/memory detail
        const phpDebug = body?._debug ?? body?.data?._debug ?? null
        if (phpDebug) {
            const phpEntryId = crypto.randomUUID()
            const rawStack   = res.config?.__devStack ?? null
            import('../stores/devLog.js').then(m => {
                const s = m.useDevLogStore()
                s.entries.unshift({
                    id:     phpEntryId,
                    time:   ts(),
                    type:   'php',
                    label:  `${meth} ${path} — ${phpDebug.queries?.length ?? 0}q`
                        + (phpDebug.http_calls?.length ? ` ${phpDebug.http_calls.length}http` : ``)
                        + ` ${phpDebug.memory_mb ?? '?'}MB`,
                    detail: phpDebug,
                    status: res.status,
                    source: src,
                    caller: null,
                })
                if (rawStack) {
                    import('./devSourceMap.js').then(({ resolveStack }) => {
                        resolveStack(rawStack).then(frames => {
                            if (!frames.length) return
                            const e = s.entries.find(x => x.id === phpEntryId)
                            if (e) e.caller = frames
                        })
                    }).catch(() => {})
                }
            }).catch(() => {})
        }

        return res
    }, err => {
        if (window.__cortexDev?.()) {
            const ms  = Date.now() - (err.config?._devT0 ?? 0)
            const st  = err.response?.status ?? 0
            const src = detectSource(err.config?.url)
            pushEntry('err',
                `${(err.config?.method ?? '?').toUpperCase()} ${cleanPath(err.config?.url ?? '?')} (${ms}ms)`,
                truncate(err.response?.data ?? { message: err.message }),
                st,
                err.config?._devId,
                src,
            )
        }
        return Promise.reject(err)
    })
}

function shortVal(a) {
    if (a === undefined) return 'undefined'
    if (a === null) return 'null'
    if (typeof a === 'function') return '[fn]'
    if (typeof a === 'string') return JSON.stringify(a.length > 80 ? a.slice(0, 80) + '\u2026' : a)
    try { const s = JSON.stringify(a); return s.length > 120 ? s.slice(0, 120) + '\u2026' : s } catch { return String(a) }
}

export function installGlobalDevLog() {
    window.devLog = (type, label, detail) => {
        if (!window.__cortexDev?.()) return
        import('../stores/devLog.js').then(m => {
            m.useDevLogStore().entries.unshift({
                id: crypto.randomUUID(), time: ts(), type: type ?? 'evt', label, detail, status: null, source: null,
            })
        }).catch(() => {})
    }

    window.__devFn = (name, args) => {
        if (!window.__cortexDev?.()) return
        const parts = (args || []).map(shortVal)
        const label = name + '(' + parts.join(', ') + ')'
        window.devLog('fn', label, { name, args: parts })
    }
}
export function attachFetchInterceptor() {
    const origFetch = window.fetch.bind(window)
    window.fetch = async function(input, init) {
        if (init === undefined) init = {}
        const url = typeof input === 'string' ? input : (input?.url ?? '')
        if (!window.__cortexDev?.()) return origFetch(input, init)

        const rawStack = new Error().stack    // captured synchronously — call chain still intact
        const method  = (init.method ?? 'GET').toUpperCase()
        const src     = detectSource(url)
        const path    = cleanPath(url)
        const devId   = crypto.randomUUID()
        const t0      = Date.now()

        let reqBody = null
        try { reqBody = init.body ? JSON.parse(init.body) : null } catch (_e) {}
        pushEntry('req', method + ' ' + path, { body: reqBody }, null, devId, src)

        // Inject X-Dev-Debug so AppendDevDebug middleware attaches DB queries
        const patchedHeaders = Object.assign({}, init.headers ?? {}, { 'X-Dev-Debug': '1' })
        const patchedInit = Object.assign({}, init, { headers: patchedHeaders })

        try {
            const response = await origFetch(input, patchedInit)
            const ms = Date.now() - t0
            const ct = response.headers.get('Content-Type') ?? ''

            if (ct.includes('text/event-stream')) {
                pushEntry('res', method + ' ' + path + ' (' + ms + 'ms) [SSE]', { streaming: true }, response.status, devId, src)
            } else if (ct.includes('application/json')) {
                try {
                    const body = await response.clone().json()
                    pushEntry('res', method + ' ' + path + ' (' + ms + 'ms)', truncate(body), response.status, devId, src)
                    const phpDebug = body?._debug ?? body?.data?._debug ?? null
                    if (phpDebug) {
                        const phpEntryId = crypto.randomUUID()
                        import('../stores/devLog.js').then(function(m) {
                            const s = m.useDevLogStore()
                            s.entries.unshift({
                                id:     phpEntryId,
                                time:   ts(),
                                type:   'php',
                                label:  method + ' ' + path + ' — ' + (phpDebug.queries?.length ?? 0) + 'q'
                                    + (phpDebug.http_calls?.length ? ' ' + phpDebug.http_calls.length + 'http' : '')
                                    + ' ' + (phpDebug.memory_mb ?? '?') + 'MB',
                                detail: phpDebug,
                                status: response.status,
                                source: src,
                                caller: null,
                            })
                            if (rawStack) {
                                import('./devSourceMap.js').then(function({ resolveStack }) {
                                    resolveStack(rawStack).then(function(frames) {
                                        if (!frames.length) return
                                        const e = s.entries.find(function(x) { return x.id === phpEntryId })
                                        if (e) e.caller = frames
                                    })
                                }).catch(function() {})
                            }
                        }).catch(function() {})
                    }
                } catch (_e) {
                    pushEntry('res', method + ' ' + path + ' (' + ms + 'ms)', { parse_error: true }, response.status, devId, src)
                }
            } else {
                pushEntry('res', method + ' ' + path + ' (' + ms + 'ms)', null, response.status, devId, src)
            }

            return response
        } catch (err) {
            const ms = Date.now() - t0
            pushEntry('err', method + ' ' + path + ' (' + ms + 'ms)', { error: err.message }, 0, devId, src)
            throw err
        }
    }
}
